/* eslint-disable max-len */
/* eslint-disable complexity */
/* eslint-disable indent */
import { ChangeDetectionStrategy, Component } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { SequenceStore } from '@sequence/sequence.store';
import { SabreTextDialogComponent } from '@shared/components/sabre-text-dialog/sabre-text-dialog.component';
import { DataItem } from '@shared/models/data-item';
import { RequestState, RequestStatus } from '@shared/models/request-state';
import { ConnectionStatusService } from '@shared/services/connection-status.service';
import { FieldConditionStore } from '@shared/services/field-condition/field-condition.store';
import { ScreenSizeService } from '@shared/services/screen-size.service';
import { FlightInfoView } from '@shared/services/station-info/models/flight-info.view';
import { StationInfoStore } from '@shared/services/station-info/station-info.store';
import { ThemeService } from '@shared/services/theme.service';
import {
  icon,
  LatLngExpression,
  Map,
  marker,
  Marker,
  tileLayer,
} from 'leaflet';
import * as lfGeo from 'leaflet.geodesic';
import { GestureHandling } from 'leaflet-gesture-handling';
import {
  BehaviorSubject,
  Observable,
  Subject,
  combineLatest,
  map,
  of,
  switchMap,
  tap,
} from 'rxjs';
import { fadeIn } from '@animations/fade-in';

@Component({
  selector: 'app-flight-info-card',
  templateUrl: './flight-info-card.component.html',
  styleUrls: ['./flight-info-card.component.scss'],
  standalone: false,
  animations: [fadeIn],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FlightInfoCardComponent {
  private _flight$ = this.sequenceStore.flight$;
  private _refresh$ = new Subject<void>(); // Triggered when refresh button clicked.
  private _isLoading$ = new BehaviorSubject(false);

  private map?: Map;
  private coords?: LatLngExpression[];
  private depMarker?: Marker;
  private arrMarker?: Marker;

  private readonly cartoKey = 'cb1_4lkx_1_5fc435807edcf7f4d4444ac7';

  departDataItems: DataItem[] = [];
  arriveDataItems: DataItem[] = [];

  mapIsReady = false;

  // Get both dep and arr station info.
  flightInfo$ = this.getFlightInfo();
  theme$ = this.themeService.theme$;
  connectionStatus$ = this.connectionStatusService.connectionStatus$.pipe(
    tap((status) => {
      this.connectionStatus = status;
    }),
  );
  screenSize$ = this.screenSizeService.screenWidth$.pipe(
    tap(() => {
      if (this.map) {
        this.initMap();
      }
    }),
  );
  isLoading$ = this._isLoading$.asObservable();
  connectionStatus: 'online' | 'offline' = 'online';

  constructor(
    private sequenceStore: SequenceStore,
    private stationInfoStore: StationInfoStore,
    private fieldConditionStore: FieldConditionStore,
    private themeService: ThemeService,
    private connectionStatusService: ConnectionStatusService,
    private screenSizeService: ScreenSizeService,
    private dialog: MatDialog,
  ) {}

  onRefresh(): void {
    this._refresh$.next();
  }

  onResetMap(): void {
    this.initMap();
  }

  onFieldReportClick(station: string): void {
    const sabreText$ = this.fieldConditionStore.getFieldCondition(station);
    const refreshFn = this.fieldConditionStore.refreshFn;

    this.dialog.open(SabreTextDialogComponent, {
      data: {
        sabreText$,
        refreshFn,
        request: station,
      },
      maxHeight: '95vh',
      maxWidth: '100vw',
    });
  }

  refreshFlightInfo(): void {
    this.flightInfo$ = this.getFlightInfo();
  }

  /** When flight is updated, fetches dep and arr station info. Triggers map update. */
  private getFlightInfo(): Observable<RequestState<FlightInfoView>> {
    return this._flight$.pipe(
      switchMap((flight) => {
        this.mapIsReady = false;

        if (!flight.response) {
          return of(flight.into<FlightInfoView>());
        }

        const { depStation, arrStation } = flight.response;
        const getDepStation$ = this.stationInfoStore.getStationInfo(depStation);
        const getArrStation$ = this.stationInfoStore.getStationInfo(arrStation);

        return combineLatest({
          departure: getDepStation$,
          arrival: getArrStation$,
        }).pipe(
          map((response) => {
            if (response.departure.isLoading || response.arrival.isLoading) {
              return new RequestState<FlightInfoView>(
                RequestStatus.IsLoadingNoResponse,
              );
            }

            if (response.departure.hasError || response.arrival.hasError) {
              return new RequestState<FlightInfoView>(
                RequestStatus.HasErrorNoResponse,
              );
            }

            if (
              !flight.response ||
              !response.departure.response ||
              !response.arrival.response
            ) {
              return new RequestState<FlightInfoView>(
                RequestStatus.IsCompleteNoResponse,
              );
            }

            this.updateCoords([
              [
                response.departure.response?.latitude || 0,
                response.departure.response?.longitude || 0,
              ],
              [
                response.arrival.response?.latitude || 0,
                response.arrival.response?.longitude || 0,
              ],
            ]);

            const timeChangeNum = flight.response!.timeChange;

            const timeChange =
              timeChangeNum > 0 ? `+${timeChangeNum}` : `${timeChangeNum}`;

            const flightInfoView: FlightInfoView = {
              timestamp: response.departure.response.timestamp,
              departure: response.departure.response,
              arrival: response.arrival.response,
              timeChange,
            };

            const depUtcOffsetNum = flight.response?.depUtcOffset || 0;
            const arrUtcOffsetNum = flight.response?.arrUtcOffset || 0;

            const depUtcOffset =
              depUtcOffsetNum > 0
                ? `+${depUtcOffsetNum}`
                : `${depUtcOffsetNum}`;
            const arrUtcOffset =
              arrUtcOffsetNum > 0
                ? `+${arrUtcOffsetNum}`
                : `${arrUtcOffsetNum}`;

            if (flightInfoView.departure) {
              // Insert timezone data from sequence.
              flightInfoView.departure.timezoneOffset = depUtcOffset;

              // Insert dep/arr times.
              flightInfoView.departure.depOrArrTimeLocal =
                flight.response.depTimeActual ||
                flight.response.depTimeResched ||
                flight.response.depTimeSched;

              flightInfoView.departure.depOrArrTimeUtc =
                flight.response.depTimeActualUTC ||
                flight.response.depTimeReschedUTC ||
                flight.response.depTimeSchedUTC;
            }

            if (flightInfoView.arrival) {
              flightInfoView.arrival.timezoneOffset = arrUtcOffset;

              flightInfoView.arrival.depOrArrTimeLocal =
                flight.response.arrTimeActual ||
                flight.response.arrTimeResched ||
                flight.response.arrTimeSched;

              flightInfoView.arrival.depOrArrTimeUtc =
                flight.response.arrTimeActualUTC ||
                flight.response.arrTimeReschedUTC ||
                flight.response.arrTimeSchedUTC;
            }

            return response.departure.into<FlightInfoView>(flightInfoView);
          }),
        );
      }),
    );
  }

  private updateCoords(latLong: LatLngExpression[]): void {
    this.coords = latLong;
    this.depMarker = marker(this.coords[0], {
      icon: icon({
        iconSize: [24, 41],
        iconAnchor: [13, 41],
        iconUrl: 'media/marker-icon-2x.png',
        iconRetinaUrl: 'media/marker-icon-2x.png',
        shadowSize: [0, 0],
      }),
    });
    this.arrMarker = marker(this.coords[1], {
      icon: icon({
        iconSize: [24, 41],
        iconAnchor: [13, 41],
        iconUrl: 'media/marker-icon-2x.png',
        iconRetinaUrl: 'media/marker-icon-2x.png',
        shadowSize: [0, 0],
      }),
    });
    this.initMap();
  }

  private initMap(): void {
    // If map is already initialized, reset it to remove any previous layers.
    if (!this.map) {
      this.map = new Map('map');
    } else {
      this.map?.remove();
      this.map = new Map('map');
    }

    // Used to hide map until ready.
    this.map.whenReady(() => {
      this.mapIsReady = true;
    });

    if (this.connectionStatus === 'offline') {
      return;
    }

    if (this.map) {
      this.map.addHandler('gestureHandling', GestureHandling);
      // @ts-expect-error typescript does not see handler.
      this.map.gestureHandling.enable();

      const tiles = tileLayer(
        `https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png?key=${this.cartoKey}`,
        {
          detectRetina: true,
          subdomains: 'abcd',
          attribution:
            '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>, &copy; <a href="https://carto.com/attributions">CARTO</a>',
          maxZoom: 20,
        },
      );

      tiles.addTo(this.map);

      this.depMarker?.addTo(this.map);
      this.arrMarker?.addTo(this.map);

      const geoLine = new lfGeo.GeodesicLine(this.coords, {
        color: 'blue',
        opacity: 0.4,
        steps: 8,
        weight: 5,
        wrap: false,
      }).addTo(this.map);
      geoLine.addTo(this.map);

      this.map.attributionControl.setPrefix('');

      this.map.fitBounds(geoLine.getBounds(), { padding: [20, 50] });
    }
  }
}
